import React, { useEffect, useMemo, useRef, useState } from "react";
import { withStyles } from "@material-ui/core/styles";
import compose from "recompose/compose";
import { useSelector } from "react-redux";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";
import { getWeeklyProgressBarData } from "../../apis/eventService";
import ApplicationUtil from "../../utils/ApplicationUtil";

function ProgressBarView(props) {
  const { classes, barView, challengeParameter } = props;

  const formatValue = (val, param) => {
    if (param === "DISTANCE") {
      const km = val / 1000;
      return km % 1 === 0 ? km.toFixed(0) : km.toFixed(2);
    }
    return Math.round(val || 0);
  };

  const unit = challengeParameter === "DISTANCE" ? "km" : "Steps";

  const userSelectedEvent = useSelector(
    (state) => state.user.userSelectedEvent,
  );

  const [openWeek, setOpenWeek] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    // Updated Mock Data with specific percentages
    const mockData = {
      progressPercentage: 100,
      currentWeek: 9,
      totalWeeks: 9,
      weeklyDots: [
        {
          week: 1,
          percentage: 0,
          weekStart: "2026-01-01",
          weekEnd: "2026-01-07",
        },
        {
          week: 2,
          percentage: 0,
          weekStart: "2026-01-08",
          weekEnd: "2026-01-14",
        },
        {
          week: 3,
          percentage: 28,
          weekStart: "2026-01-15",
          weekEnd: "2026-01-21",
        },
        {
          week: 4,
          percentage: 64,
          weekStart: "2026-01-22",
          weekEnd: "2026-01-28",
        },
        {
          week: 5,
          percentage: 20,
          weekStart: "2026-01-29",
          weekEnd: "2026-02-04",
        },
        {
          week: 6,
          percentage: 60,
          weekStart: "2026-02-05",
          weekEnd: "2026-02-11",
        },
        {
          week: 7,
          percentage: 0,
          weekStart: "2026-02-12",
          weekEnd: "2026-02-18",
        },
        {
          week: 8,
          percentage: 22,
          weekStart: "2026-02-19",
          weekEnd: "2026-02-25",
        },
        {
          week: 9,
          percentage: 0,
          weeklyTarget: 0,
          weekStart: "2026-02-26",
          weekEnd: "2026-02-28",
        },
      ],
      distanceCovered: 97000,
      targetDistance: 50000,
    };

    const fetchData = async () => {
      let runnerData = ApplicationUtil.getRunnerDetails();
      let runnerId = runnerData?.runner?.id;
      // Logic to fetch real data...
      const result = await getWeeklyProgressBarData(
        userSelectedEvent?.id,
        runnerId,
        barView,
      );
      setData(result?.data || result);

      // Using mock data for demonstration
      // setData(mockData);
    };

    if (userSelectedEvent?.id || true) fetchData();
  }, [userSelectedEvent]);

  const processedDots = useMemo(() => {
    if (!data?.weeklyDots) return [];

    let runningTotal = 0;
    let targetTotal = 0;
    return data.weeklyDots.map((dot) => {
      const weekTarget = dot.weeklyTarget || dot.earned || 0;

      runningTotal += dot.percentage;
      targetTotal += weekTarget;
      return {
        ...dot,
        cumulativePosition: Math.min(runningTotal, 100),
        cumulativeTarget: Math.min(targetTotal, data?.targetDistance)
      };
    });
  }, [data]);

  const progressStyle = useMemo(
    () => ({
      width: `${data?.progressPercentage || 0}%`,
      background: "linear-gradient(90deg, #0B3F63 0%, #36729eff 100%)",
      borderRadius: "20px",
      height: "100%",
      transition: "width 1s ease-in",
      boxShadow: "0 2px 8px rgba(15, 23, 42, 0.12)",
    }),
    [data],
  );

  const handleDotClick = (weekNum) => {
    setOpenWeek(openWeek === weekNum ? null : weekNum);
  };

  if (!data) return null;

  const getBubbleTransformX = (leftPercent, isLast = false) => {
    if (isLast) return "-92%";
    if (leftPercent <= 10) return "-4%";
    if (leftPercent >= 90) return "-84%";
    return "-50%";
  };

  return (
    <div className={classes.progressBar}>
      <div className={classes.progress}>
        {/* The Colored Bar */}
        <div style={progressStyle} />

        {/* Weekly Dots Rendered via Cumulative Logic */}
        {processedDots.map((dot, index) => {
          const isCurrentWeek = dot.week === data.currentWeek;
          const isOpen = openWeek === dot.week;

          const iconColor = "#0B3F63";
          let left = `${dot.cumulativePosition}%`;
          if (dot.cumulativePosition <= 5) left = "2%";
          if (dot.cumulativePosition >= 97) left = "98%";

          const isLast = index === processedDots.length - 1;

          const translateX = getBubbleTransformX(
            dot.cumulativePosition,
            isLast,
          );

          const arrowPosStyle = {
            left:
              dot.cumulativePosition <= 10
                ? "14px"
                : isLast || dot.cumulativePosition >= 90
                  ? "calc(100% - 14px)"
                  : "50%",
            transform:
              dot.cumulativePosition <= 10
                ? "translateX(-50%)"
                : isLast
                  ? "translateX(-40%)"
                  : dot.cumulativePosition >= 90
                    ? "translateX(-100%)"
                    : "translateX(-50%)",
          };
          return (
            <div
              key={index}
              className={classes.targetIconWrapper}
              style={{
                left: left,
                zIndex: isOpen ? 20 : 6 + index,
              }}
            >
              <div
                className={classes.targetIcon}
                onClick={() => handleDotClick(dot.week)}
              >
                <TrackChangesIcon style={{ fontSize: 14, color: iconColor }} />
              </div>

              {/* Tooltip Popup */}
              {isOpen && (
                <div
                  className={classes.weekBubble}
                  style={{
                    left,
                    transform: `translate(${translateX}, -100%)`,
                    zIndex: 9999,
                  }}
                >
                  <div className={classes.weekBubbleTitle}>Week {dot.week}</div>
                  <div className={classes.weekBubbleSub}>
                    {new Date(dot.weekStart).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                    })}{" "}
                    -{" "}
                    {new Date(dot.weekEnd).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                    })}
                  </div>

                  {typeof dot.weeklyTarget === "number" && (
                    <div className={classes.weekBubbleSub}>
                      Target:{" "}
                      {formatValue(dot.cumulativeTarget, challengeParameter)}{" "}
                      {unit}
                    </div>
                  )}

                  {/* <div className={classes.weekBubbleSub}>
                    Contribution: {dot.percentage}%
                  </div> */}
                  {/* Optional: Show cumulative total in tooltip */}
                  {/* <div className={classes.weekBubbleSub}>
                       Total: {dot.cumulativePosition}%
                    </div> */}
                  <div
                    className={classes.weekBubbleArrow}
                    style={arrowPosStyle}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className={classes.valueLayout}>
        {/* <span>0%</span> */}
        <span>{data?.progressPercentage || 0}% Completed</span>
        {/* <span>{formatValue(data.distanceCovered, challengeParameter)}{unit}</span> */}
        <span>
          {formatValue(data.targetDistance, challengeParameter)}{" "}
          {unit}
          <p
            style={{
              margin: 0,
              fontSize: "12px",
            }}
          >
            Total target
          </p>
        </span>
      </div>
    </div>
  );
}

const styles = (theme) => ({
  progressBar: {
    width: "100%",
    marginTop: "30px",
    marginBottom: "30px",
    paddingTop: 50,
    boxSizing: "border-box",
  },
  progress: {
    backgroundColor: "#E3E9F0",
    borderRadius: "20px",
    height: "30px",
    width: "100%",
    position: "relative",
    display: "flex",
    alignItems: "center",
    overflow: "visible",
    border: "1px solid rgba(15, 23, 42, 0.16)",
  },
  targetIconWrapper: {
    position: "absolute",
    top: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  targetIcon: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "scale(1.2)",
      zIndex: 25,
    },
  },
  weekBubble: {
    position: "absolute",
    top: "-12px",
    backgroundColor: "#09456c",
    color: "#fff",
    padding: "10px 12px",
    borderRadius: "10px",
    fontSize: "12px",
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    boxShadow: "0 10px 18px rgba(0,0,0,0.22)",
    pointerEvents: "auto",
    border: "1px solid rgba(255,255,255,0.25)",
    cursor: "pointer",
    [theme.breakpoints.down("sm")]: {
      padding: "6px 8px",
      fontSize: "10px",
      borderRadius: "8px",
      top: "-8px",
    },
  },
  weekBubbleTitle: {
    fontWeight: "bold",
    marginBottom: "2px",
  },
  weekBubbleSub: {
    fontSize: "11px",
    color: "#CDE7FF",
  },
  weekBubbleArrow: {
    position: "absolute",
    bottom: "-8px",
    width: 0,
    height: 0,
    borderLeft: "8px solid transparent",
    borderRight: "8px solid transparent",
    borderTop: "8px solid #09456c",
    [theme.breakpoints.down("sm")]: {
      bottom: "-6px",
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      borderTop: "6px solid #09456c",
    },
  },
  valueLayout: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    marginTop: "10px",
    color: "#0B3F63",
    fontWeight: "bold",
    fontSize: "12px",
  },
});

export default compose(withStyles(styles, { withTheme: true }))(
  ProgressBarView,
);
